import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const Process4 = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const buttonsContainerRef = useRef(null);
  const buttonRefs = useRef([]);

  const steps = [
    {
      title: 'Meeting',
      subtitle: 'Initial Consultation',
      description: 'We begin with a comprehensive discussion to understand your business goals, challenges, and vision for success on Amazon.',
      benefits: [
        { icon: '', title: 'In-depth business analysis' },
        { icon: '', title: 'Clear goal setting' },
        { icon: '', title: 'Challenge identification' },
        { icon: '', title: 'Market opportunity assessment' }
      ],
      color: 'from-blue-600/20 via-indigo-600/20 to-purple-600/20'
    },
    {
      title: 'Account Audit',
      subtitle: 'Performance Analysis',
      description: 'Our team conducts a thorough analysis of your Amazon presence, identifying key areas for improvement and growth.',
      benefits: [
        { icon: '', title: 'Listing optimization check' },
        { icon: '', title: 'Competitor analysis' },
        { icon: '', title: 'Performance metrics review' },
        { icon: '', title: 'Growth opportunity identification' }
      ],
      color: 'from-purple-600/20 via-pink-600/20 to-red-600/20'
    },
    {
      title: 'Account Report',
      subtitle: 'Strategic Insights',
      description: 'We present a detailed report with actionable insights and recommendations for your Amazon business.',
      benefits: [
        { icon: '', title: 'Performance breakdown' },
        { icon: '', title: 'Strategic recommendations' },
        { icon: '', title: 'Priority actions' },
        { icon: '', title: 'Growth projections' }
      ],
      color: 'from-red-600/20 via-orange-600/20 to-yellow-600/20'
    },
    {
      title: 'Project Discussion',
      subtitle: 'Strategy Development',
      description: 'Together, we develop a comprehensive strategy tailored to your specific needs and goals.',
      benefits: [
        { icon: '', title: 'Custom strategy creation' },
        { icon: '', title: 'Timeline planning' },
        { icon: '', title: 'Resource allocation' },
        { icon: '', title: 'KPI definition' }
      ],
      color: 'from-yellow-600/20 via-green-600/20 to-emerald-600/20'
    },
    {
      title: 'OnBoarding',
      subtitle: 'Implementation Phase',
      description: 'We begin implementing our proven systems and processes to optimize your Amazon presence.',
      benefits: [
        { icon: '', title: 'System setup' },
        { icon: '', title: 'Team integration' },
        { icon: '', title: 'Process implementation' },
        { icon: '', title: 'Initial optimizations' }
      ],
      color: 'from-emerald-600/20 via-teal-600/20 to-cyan-600/20'
    },
    {
      title: "It's a Team's Perspective",
      subtitle: 'Collaborative Success',
      description: 'Our team works closely with yours to ensure alignment and maximize results.',
      benefits: [
        { icon: '', title: 'Regular communication' },
        { icon: '', title: 'Knowledge sharing' },
        { icon: '', title: 'Team coordination' },
        { icon: '', title: 'Progress tracking' }
      ],
      color: 'from-cyan-600/20 via-blue-600/20 to-indigo-600/20'
    },
    {
      title: 'Flourish',
      subtitle: 'Sustainable Growth',
      description: 'Watch your Amazon business reach new heights with our ongoing optimization and support.',
      benefits: [
        { icon: '', title: 'Continuous optimization' },
        { icon: '', title: 'Performance monitoring' },
        { icon: '', title: 'Market expansion' },
        { icon: '', title: 'Long-term growth' }
      ],
      color: 'from-indigo-600/20 via-violet-600/20 to-purple-600/20'
    }
  ];

  // Setup button refs
  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(0, steps.length);
  }, []);

  // Auto-scroll to active button
  useEffect(() => {
    if (buttonRefs.current[activeStep] && buttonsContainerRef.current) {
      const container = buttonsContainerRef.current;
      const button = buttonRefs.current[activeStep];
      const containerRect = container.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();

      const scrollLeft = container.scrollLeft + buttonRect.left - containerRect.left - (containerRect.width - buttonRect.width) / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [activeStep]);

  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setActiveStep((current) => (current + 1) % steps.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, steps.length]);

  return (
    <section className="bg-cream dark:bg-black text-gray-900 dark:text-white py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-block bg-primary/20 text-primary px-6 py-2 rounded-full text-sm font-medium mb-6">
            THE PROCESS
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            Your Journey to
            <span className="block text-primary mt-2">Amazon Success</span>
          </h2>
        </div>

        {/* Process Display */}
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Step Navigation - Left Side */}
            <div 
              ref={buttonsContainerRef}
              className="lg:col-span-3 flex lg:flex-col gap-4 overflow-x-auto lg:overflow-x-visible pb-4 lg:pb-0 -mx-4 lg:mx-0 px-4 lg:px-0 scroll-smooth"
            >
              {steps.map((step, index) => (
                <button
                  key={index}
                  ref={el => buttonRefs.current[index] = el}
                  onClick={() => {
                    setActiveStep(index);
                    setIsAutoPlaying(false);
                  }}
                  className={`relative flex-shrink-0 lg:flex-shrink w-auto lg:w-full px-6 py-4 rounded-xl font-medium transition-all duration-300 overflow-hidden group text-left ${
                    activeStep === index
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3 whitespace-nowrap lg:whitespace-normal">
                    <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                      activeStep === index ? 'bg-primary text-white border border-white' : 'bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-white/60'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="relative z-10">{step.title}</span>
                  </div>
                  {activeStep === index && (
                    <motion.div
                      layoutId="highlight"
                      className="absolute inset-0 bg-primary"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Step Content - Right Side */}
            <div className="lg:col-span-9">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className={`bg-gradient-to-br ${steps[activeStep].color} rounded-3xl p-6 lg:p-12 backdrop-blur-sm border border-gray-200/20 dark:border-white/10`}
                >
                  {/* Content */}
                  <div>
                    <div className="text-primary mb-2">Step {activeStep + 1}</div>
                    <h3 className="text-2xl lg:text-3xl font-bold mb-2">{steps[activeStep].title}</h3>
                    <div className="text-base lg:text-lg text-gray-700 dark:text-white/80 mb-6">{steps[activeStep].subtitle}</div>
                    <p className="text-gray-600 dark:text-white/60 mb-8">{steps[activeStep].description}</p>

                    {/* Benefits Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mb-6">
                      {steps[activeStep].benefits.map((benefit, idx) => (
                        <div
                          key={idx}
                          className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-xl p-4 hover:bg-white/90 dark:hover:bg-white/10 transition-all duration-300"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-primary text-xl">{benefit.icon}</span>
                            <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">{benefit.title}</h4>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-4 mt-8">
                      <Link
                        to="/contact"
                        className="inline-block px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors duration-300"
                      >
                        GET A FREE AUDIT
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Controls Below Content */}
              <div className="flex justify-between items-center mt-6">
                {/* Progress Dots */}
                <div className="hidden lg:flex space-x-2">
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        activeStep === index ? 'bg-primary w-8' : 'bg-gray-400 dark:bg-white/20'
                      }`}
                    />
                  ))}
                </div>

                {/* Mobile Progress Text */}
                <div className="lg:hidden text-gray-600 dark:text-white/60 text-sm">
                  Step {activeStep + 1} of {steps.length}
                </div>

                {/* Autoplay Toggle */}
                <button
                  onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                  className={`p-2 rounded-full transition-colors duration-300 ${
                    isAutoPlaying ? 'bg-primary/20 text-primary' : 'bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-white/60'
                  }`}
                >
                  {isAutoPlaying ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process4;
